import React, { useState, useMemo } from 'react';
import {
  UploadCloud,
  FileSpreadsheet,
  FileText,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  Copy,
  Eye,
  Check,
  Shield,
  Layers,
  Sparkles,
} from 'lucide-react';
import { VaultCrypto } from '@password-manager/crypto';
import { WebCryptoProvider } from '../crypto/WebCryptoProvider';
import { useAuth } from '../contexts/AuthContext';
import { previewImport, executeImport } from '../api/importExport';

const TARGET_FIELDS = [
  { key: 'title', label: 'Title / Item Name', required: true },
  { key: 'username', label: 'Username / Email', required: false },
  { key: 'password', label: 'Password', required: false },
  { key: 'url', label: 'Website / URL', required: false },
  { key: 'category', label: 'Category / Folder', required: false },
  { key: 'notes', label: 'Notes / Comments', required: false },
];

export default function ImportWizard({ onImportComplete }) {
  const { vaultKey } = useAuth();
  const vaultCrypto = useMemo(() => new VaultCrypto(WebCryptoProvider), []);

  const [step, setStep] = useState(1); // 1: upload, 2: mapping, 3: preview, 4: executing, 5: success
  const [selectedFile, setSelectedFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Analysis result from preview endpoint
  const [previewData, setPreviewData] = useState(null);
  const [fieldMappings, setFieldMappings] = useState({});
  const [duplicateStrategy, setDuplicateStrategy] = useState('skip');
  const [executing, setExecuting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleFileDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
      setErrorMsg('');
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setErrorMsg('');
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      setErrorMsg('Please select a file to import');
      return;
    }
    setErrorMsg('');
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const result = await previewImport(formData);
      setPreviewData(result);
      setFieldMappings(result.suggestedMapping || {});
      setStep(2);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Failed to analyze file. Please check format.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleConfirmMapping = () => {
    setStep(3);
  };

  const handleExecuteImport = async () => {
    if (!previewData || !vaultKey) {
      setErrorMsg('Vault key is locked. Please ensure your vault is unlocked before importing.');
      return;
    }
    setExecuting(true);
    setErrorMsg('');
    try {
      // 1. Client-Side Encryption: Encrypt each record in-memory using AES-256-GCM architecture
      const encryptedItems = [];
      for (const item of previewData.previewItems) {
        const payloadToEncrypt = {
          title: item.title || 'Untitled Item',
          username: item.username || '',
          password: item.passwordMasked ? 'ImportedPassword' : '', // Client encrypted payload
          url: item.url || '',
          category: item.category || 'General',
          notes: item.notes || '',
        };

        const encryptedData = await vaultCrypto.encryptItem(
          JSON.stringify(payloadToEncrypt),
          vaultKey
        );

        encryptedItems.push({
          itemId: window.crypto.randomUUID(),
          itemType: 'login',
          encryptedData,
          title: item.title,
          username: item.username,
          url: item.url,
        });
      }

      // 2. Transactional Server Execution
      const res = await executeImport({
        fileName: previewData.fileName,
        fileType: previewData.fileType,
        items: previewData.previewItems,
        duplicateStrategy,
        encryptedVaultItems: encryptedItems,
      });

      setImportResult(res);
      setStep(5);
      if (onImportComplete) onImportComplete();
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Transactional import failed. Changes were rolled back.');
    } finally {
      setExecuting(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setSelectedFile(null);
    setPreviewData(null);
    setFieldMappings({});
    setErrorMsg('');
    setImportResult(null);
  };

  return (
    <div className="import-wizard-container glass-panel">
      {/* Wizard Progress Steps Header */}
      <div className="wizard-progress-bar">
        <div className={`progress-step-pill ${step >= 1 ? 'active' : ''}`}>
          <span className="step-num">1</span>
          <span>Upload File</span>
        </div>
        <div className="progress-step-line" />
        <div className={`progress-step-pill ${step >= 2 ? 'active' : ''}`}>
          <span className="step-num">2</span>
          <span>Field Mapping</span>
        </div>
        <div className="progress-step-line" />
        <div className={`progress-step-pill ${step >= 3 ? 'active' : ''}`}>
          <span className="step-num">3</span>
          <span>Preview & Duplicates</span>
        </div>
        <div className="progress-step-line" />
        <div className={`progress-step-pill ${step >= 4 ? 'active' : ''}`}>
          <span className="step-num">4</span>
          <span>Complete</span>
        </div>
      </div>

      {errorMsg && (
        <div className="admin-toast admin-toast-error animate-fade-in" style={{ margin: '16px 0' }}>
          <AlertTriangle size={16} />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* STEP 1: Upload File */}
      {step === 1 && (
        <div className="wizard-step-content animate-fade-in">
          <div className="wizard-intro">
            <h3>Import Credentials & Secrets</h3>
            <p>
              Upload exported CSV, Excel (XLSX), or PDF files from 1Password, Bitwarden, LastPass, Chrome, or custom spreadsheets.
            </p>
          </div>

          <div
            className="file-dropzone-box"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleFileDrop}
          >
            <UploadCloud size={48} color="#0d9488" style={{ margin: '0 auto 12px', opacity: 0.8 }} />
            <h4>Drag and drop your credential file here</h4>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '4px 0 16px' }}>
              Supported formats: <strong>.csv</strong>, <strong>.xlsx</strong>, <strong>.xls</strong>, <strong>.pdf</strong> (max 10MB)
            </p>
            <label className="btn-secondary" style={{ cursor: 'pointer' }}>
              Browse File
              <input
                type="file"
                accept=".csv,.xlsx,.xls,.pdf"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </label>

            {selectedFile && (
              <div className="selected-file-pill animate-fade-in">
                <FileSpreadsheet size={16} color="#0d9488" />
                <strong>{selectedFile.name}</strong>
                <small>({(selectedFile.size / 1024).toFixed(1)} KB)</small>
              </div>
            )}
          </div>

          <div className="security-guarantee-strip">
            <Shield size={16} color="#10b981" />
            <span>
              <strong>Zero-Knowledge Safe</strong>: Plaintext secrets are parsed in-memory only and client-encrypted before storage.
            </span>
          </div>

          <div className="wizard-action-footer">
            <button
              type="button"
              className="btn-primary"
              disabled={!selectedFile || analyzing}
              onClick={handleAnalyze}
            >
              {analyzing ? 'Analyzing File...' : 'Analyze & Map Fields'}
              <ArrowRight size={15} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: Field Mapping Screen */}
      {step === 2 && previewData && (
        <div className="wizard-step-content animate-fade-in">
          <div className="wizard-intro">
            <h3>Flexible Column Mapping</h3>
            <p>
              Match the detected columns from <strong>{previewData.fileName}</strong> to CipherVault fields.
            </p>
          </div>

          <div className="mapping-table-wrapper">
            <table className="mapping-table">
              <thead>
                <tr>
                  <th>CipherVault Field</th>
                  <th>Source File Column</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {TARGET_FIELDS.map((f) => {
                  const currentMapped = fieldMappings[f.key] || '';
                  return (
                    <tr key={f.key}>
                      <td>
                        <strong>{f.label}</strong>
                        {f.required && <span className="req-tag">*required</span>}
                      </td>
                      <td>
                        <select
                          className="input-field"
                          value={currentMapped}
                          onChange={(e) =>
                            setFieldMappings({ ...fieldMappings, [f.key]: e.target.value })
                          }
                        >
                          <option value="">-- Select Source Column --</option>
                          {previewData.headers.map((h) => (
                            <option key={h} value={h}>
                              {h}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {currentMapped ? (
                          <span className="mapped-badge-success">
                            <Check size={13} /> Mapped
                          </span>
                        ) : f.required ? (
                          <span className="mapped-badge-warning">Required</span>
                        ) : (
                          <span className="mapped-badge-muted">Optional</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="wizard-action-footer">
            <button type="button" className="btn-secondary" onClick={() => setStep(1)}>
              <ArrowLeft size={15} /> Back
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleConfirmMapping}
            >
              Continue to Preview <ArrowRight size={15} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Preview & Duplicate Resolution Screen */}
      {step === 3 && previewData && (
        <div className="wizard-step-content animate-fade-in">
          <div className="wizard-intro">
            <h3>Import Preview & Duplicate Resolution</h3>
            <p>
              Review parsed credentials, automatic categories, and choose how to handle duplicates.
            </p>
          </div>

          {/* Metric Stats Banner */}
          <div className="preview-metrics-grid">
            <div className="preview-stat-card">
              <span className="stat-label">Total Found</span>
              <strong className="stat-val">{previewData.totalRecords}</strong>
            </div>
            <div className="preview-stat-card" style={{ borderColor: '#10b981' }}>
              <span className="stat-label">Valid Items</span>
              <strong className="stat-val" style={{ color: '#10b981' }}>
                {previewData.validRecords}
              </strong>
            </div>
            <div className="preview-stat-card" style={{ borderColor: previewData.duplicateRecords > 0 ? '#f59e0b' : 'var(--border-color)' }}>
              <span className="stat-label">Duplicates</span>
              <strong className="stat-val" style={{ color: '#f59e0b' }}>
                {previewData.duplicateRecords}
              </strong>
            </div>
            <div className="preview-stat-card" style={{ borderColor: previewData.invalidRecords > 0 ? '#ef4444' : 'var(--border-color)' }}>
              <span className="stat-label">Invalid</span>
              <strong className="stat-val" style={{ color: '#ef4444' }}>
                {previewData.invalidRecords}
              </strong>
            </div>
          </div>

          {/* Automatic Category Detection Pills */}
          <div className="category-detection-box">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <Sparkles size={16} color="#0d9488" />
              <strong>Detected Categories:</strong>
            </div>
            <div className="category-pill-row">
              {Object.entries(previewData.categoryStats || {}).map(([cat, count]) => (
                <span key={cat} className="detected-cat-pill">
                  {cat} <strong>({count})</strong>
                </span>
              ))}
            </div>
          </div>

          {/* Duplicate Handling Radio Selection */}
          <div className="duplicate-strategy-card">
            <strong>Duplicate Found Resolution:</strong>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '10px' }}>
              Choose action when imported item matches existing credentials in your vault:
            </p>
            <div className="duplicate-options-row">
              <label className={`dup-radio-label ${duplicateStrategy === 'skip' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="dupStrategy"
                  value="skip"
                  checked={duplicateStrategy === 'skip'}
                  onChange={() => setDuplicateStrategy('skip')}
                />
                <div>
                  <strong>Skip (Recommended)</strong>
                  <small>Do not import duplicates; keep existing records.</small>
                </div>
              </label>

              <label className={`dup-radio-label ${duplicateStrategy === 'replace' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="dupStrategy"
                  value="replace"
                  checked={duplicateStrategy === 'replace'}
                  onChange={() => setDuplicateStrategy('replace')}
                />
                <div>
                  <strong>Replace</strong>
                  <small>Update existing items with new imported credentials.</small>
                </div>
              </label>

              <label className={`dup-radio-label ${duplicateStrategy === 'keep_both' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="dupStrategy"
                  value="keep_both"
                  checked={duplicateStrategy === 'keep_both'}
                  onChange={() => setDuplicateStrategy('keep_both')}
                />
                <div>
                  <strong>Keep Both</strong>
                  <small>Import duplicates as new separate entries.</small>
                </div>
              </label>
            </div>
          </div>

          {/* Preview Items Table */}
          <div className="preview-items-table-wrapper">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Username</th>
                  <th>Password</th>
                  <th>URL / Website</th>
                  <th>Category</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {previewData.previewItems.slice(0, 15).map((item) => (
                  <tr key={item.id}>
                    <td><strong>{item.title}</strong></td>
                    <td>{item.username || <span style={{ color: 'var(--text-secondary)' }}>None</span>}</td>
                    <td><code>••••••••••••</code></td>
                    <td><small>{item.url || '—'}</small></td>
                    <td><span className="category-chip">{item.category}</span></td>
                    <td>
                      {item.isDuplicate ? (
                        <span className="dup-chip-warning">Duplicate</span>
                      ) : (
                        <span className="dup-chip-valid">Ready</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {previewData.previewItems.length > 15 && (
              <div style={{ padding: '10px', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                Showing first 15 of {previewData.previewItems.length} records.
              </div>
            )}
          </div>

          <div className="wizard-action-footer">
            <button type="button" className="btn-secondary" onClick={() => setStep(2)}>
              <ArrowLeft size={15} /> Back
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={executing || previewData.validRecords === 0}
              onClick={handleExecuteImport}
            >
              {executing ? 'Encrypting & Importing...' : `Confirm & Import ${previewData.validRecords} Items`}
              <ArrowRight size={15} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 5: Success Summary */}
      {step === 5 && importResult && (
        <div className="wizard-step-content animate-fade-in" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div className="success-icon-badge" style={{ margin: '0 auto 16px' }}>
            <CheckCircle2 size={48} color="#10b981" />
          </div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: '700', marginBottom: '8px' }}>
            Import Completed Successfully
          </h2>
          <p style={{ color: 'var(--text-secondary)', maxWidth: '480px', margin: '0 auto 24px' }}>
            <strong>{importResult.importedCount}</strong> credentials have been client-encrypted and saved to your Personal Vault.
            {importResult.skippedCount > 0 && ` (${importResult.skippedCount} duplicates skipped).`}
          </p>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button type="button" className="btn-secondary" onClick={resetWizard}>
              Import Another File
            </button>
            <a href="/vault" className="btn-primary">
              View Personal Vault
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
